import type { ReactNode } from "react";

import { ChevronDownIcon, ChevronUpIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface ReviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Navbatdagi o'rni (0 dan boshlab) va joriy sahifadagi jami. */
  position?: { index: number; total: number };
  onPrevious?: () => void;
  onNext?: () => void;
  children: ReactNode;
}

/**
 * Navbat elementini ko'rib chiqish paneli — o'ngdan chiqadi.
 *
 * Jadval to'liq enda qoladi, element esa keng panelda o'qiladi. Yuqoridagi
 * strelkalar navbat bo'ylab yuradi; qaror tugmalari panel ichida pastga
 * yopishib turadi (panellarning o'zida).
 */
export const ReviewSheet = ({
  open,
  onOpenChange,
  title,
  description,
  position,
  onPrevious,
  onNext,
  children,
}: ReviewSheetProps) => {
  const { t } = useTranslation();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        // Tavsif bo'lmasa Radix ogohlantirmasin — nom `SheetTitle` da bor.
        {...(description ? {} : { "aria-describedby": undefined })}
        className="gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-2xl"
      >
        <SheetHeader className="flex-row items-start justify-between gap-3 border-b px-5 py-3.5">
          <div className="min-w-0">
            <SheetTitle className="truncate text-base font-semibold">{title}</SheetTitle>
            {description && <SheetDescription className="truncate">{description}</SheetDescription>}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {position && position.total > 0 && position.index >= 0 && (
              <span className="text-muted-foreground me-1 text-xs tabular-nums">
                {t("COMMON.PAGE_OF", { page: position.index + 1, total: position.total })}
              </span>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={!onPrevious}
              onClick={onPrevious}
              aria-label={t("REVIEW_SHEET.PREVIOUS")}
            >
              <ChevronUpIcon />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={!onNext}
              onClick={onNext}
              aria-label={t("REVIEW_SHEET.NEXT")}
            >
              <ChevronDownIcon />
            </Button>
            <SheetClose asChild>
              <Button variant="ghost" size="icon-sm" aria-label={t("COMMON.CLOSE")}>
                <XIcon />
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </SheetContent>
    </Sheet>
  );
};
